import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from "flowbite-react"
import { Clock, Heart, MicrophoneSpeaking, NavArrowRight, Shield, ShieldCheck } from "iconoir-react"
import mixpanel from "mixpanel-browser"
import { useEffect, useRef, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import DemoCallModal from "./DemoCallModal"

export default function IntroModal({
  show,
  setShow,
  onClose,
  email
}: {
  show: boolean
  setShow: (show: boolean) => void
  onClose: () => void
  email: string
}) {
  const [showOverride, setShowOverride] = useState(show)
  const [showDemoModal, setShowDemoModal] = useState(false)
  const modalRef = useRef<HTMLButtonElement>(null)
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    setShowOverride(show)
  }, [show])

  const isOnSpecialPage = () => {
    return (
      location.pathname.includes("dynamic-task") ||
      location.pathname.includes("chat") ||
      location.pathname.match(/\/calls\/\d+\/edit/)
    )
  }

  const onCloseOverride = () => {
    window.localStorage.setItem(`show_intro_modal:${email}`, "false")
    setShow(false)
    setShowOverride(false)

    mixpanel.track("Product Tour Completed (frontend)", {
      email: email
    })

    onClose()
    if (!isOnSpecialPage()) {
      navigate("/new-call")
    }
  }

  return (
    <>
      <Modal show={showOverride} size="5xl" initialFocus={modalRef} onClose={onCloseOverride} className="pt-safe">
        <ModalHeader className="border-b border-gray-200/80 px-6 py-4">
          <div className="flex justify-between items-center w-full">
            <img src="/vibrato-logo.svg" alt="Vibrato Logo" className="h-8 w-auto" />
          </div>
        </ModalHeader>

        <ModalBody className="px-6 py-8 modal-body">
          <div className="space-y-8">
            <div className="flex flex-col lg:flex-row gap-8">
              <div className="flex-1 space-y-6">
                <div className="text-center lg:text-left space-y-4">
                  <h2 className="text-3xl lg:text-4xl font-bold bg-linear-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                    Your AI Phone Assistant
                  </h2>
                  <p className="text-xl text-gray-600 leading-relaxed">
                    {isOnSpecialPage()
                      ? "Meet Vibrato, your AI assistant that effortlessly manages calls and tasks. Close this popup to finish your task."
                      : "Let AI handle your calls, negotiations, and customer service while you focus on what matters most."}
                  </p>
                  <div className="flex flex-wrap gap-3 justify-center lg:justify-start">
                    <span className="bg-green-100 text-green-800 text-sm font-semibold px-3 py-1 rounded-full">
                      Start Free
                    </span>
                    <span className="bg-blue-100 text-blue-800 text-sm font-semibold px-3 py-1 rounded-full">
                      30 Minutes Included
                    </span>
                    <span className="bg-purple-100 text-purple-800 text-sm font-semibold px-3 py-1 rounded-full flex items-center gap-1.5">
                      <Shield className="w-4 h-4" />
                      Enterprise-Grade Security
                    </span>
                  </div>
                </div>

                <div className="bg-linear-to-br from-purple-50 to-purple-100 rounded-xl p-6 shadow-sm border border-purple-200">
                  <div className="flex items-center gap-3 mb-4">
                    <MicrophoneSpeaking className="w-12 h-12 text-purple-800" />
                    <div>
                      <h4 className="text-xl font-bold text-purple-800">Get 20 Extra Minutes Free!</h4>
                      <p className="text-purple-700">Take a quick demo call now</p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="grid gap-4">
                      <div className="flex items-start gap-3">
                        <span className="p-1.5 bg-purple-100 rounded-full">
                          <Clock className="w-5 h-5 text-purple-600" />
                        </span>
                        <div>
                          <span className="font-semibold text-purple-800">Experience AI Calling</span>
                          <p className="text-purple-700">See how our technology works in real-time</p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <span className="p-1.5 bg-purple-100 rounded-full">
                          <Heart className="w-5 h-5 text-purple-600" />
                        </span>
                        <div>
                          <span className="font-semibold text-purple-800">$4.99 Value</span>
                          <p className="text-purple-700">Unlock 20 additional minutes instantly</p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <span className="p-1.5 bg-purple-100 rounded-full">
                          <ShieldCheck className="w-5 h-5 text-purple-600" />
                        </span>
                        <div>
                          <span className="font-semibold text-purple-800">SOC 2 Type I Certified</span>
                          <p className="text-purple-700">Your data is protected by enterprise-grade security</p>
                        </div>
                      </div>
                    </div>

                    <button
                      onClick={() => setShowDemoModal(true)}
                      className="w-full mt-4 inline-flex items-center justify-center px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors gap-2 font-semibold"
                    >
                      Start Your Demo Call
                      <NavArrowRight className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex justify-center items-center flex-1">
                <div className="w-full max-w-[500px] space-y-4">
                  <h3 className="text-2xl font-bold text-gray-800 text-center mb-6">What You Can Do Right Now</h3>

                  <div className="space-y-3">
                    <div className="flex items-start gap-3 p-4 bg-white rounded-lg shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                      <span className="text-2xl">💰</span>
                      <div>
                        <h4 className="font-semibold text-gray-800">Lower Your Cable Bill</h4>
                        <p className="text-sm text-gray-600">AI negotiates with providers to save you $50-200/month</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3 p-4 bg-white rounded-lg shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                      <span className="text-2xl">📞</span>
                      <div>
                        <h4 className="font-semibold text-gray-800">Skip Tech Support Hold</h4>
                        <p className="text-sm text-gray-600">
                          AI waits on hold for hours, calls you when human picks up
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3 p-4 bg-white rounded-lg shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                      <span className="text-2xl">💳</span>
                      <div>
                        <h4 className="font-semibold text-gray-800">Remove Credit Card Late Fee</h4>
                        <p className="text-sm text-gray-600">Challenge unfair charges and get $25-40+ back instantly</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3 p-4 bg-white rounded-lg shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                      <span className="text-2xl">🚗</span>
                      <div>
                        <h4 className="font-semibold text-gray-800">Reduce Car Insurance Premium</h4>
                        <p className="text-sm text-gray-600">Find better rates and save hundreds per year</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3 p-4 bg-white rounded-lg shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                      <span className="text-2xl">❌</span>
                      <div>
                        <h4 className="font-semibold text-gray-800">Cancel Unwanted Subscriptions</h4>
                        <p className="text-sm text-gray-600">End recurring payments and stop forgotten charges</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3 p-4 bg-white rounded-lg shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                      <span className="text-2xl">💊</span>
                      <div>
                        <h4 className="font-semibold text-gray-800">Request Prescription Refill</h4>
                        <p className="text-sm text-gray-600">Handle pharmacy calls without waiting in line</p>
                      </div>
                    </div>
                  </div>

                  <div className="text-center mt-6">
                    <p className="text-sm text-gray-500 font-medium">500+ other tasks available</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </ModalBody>

        <ModalFooter className="border-t border-gray-200/80 px-6 py-4">
          <div className="flex justify-between w-full">
            <div className="flex items-center gap-4 ml-auto">
              <Button color="blue" onClick={onCloseOverride} className="flex items-center gap-2" ref={modalRef as any}>
                Get Started
                <NavArrowRight className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </ModalFooter>
      </Modal>

      <DemoCallModal showModal={showDemoModal} setShowModal={setShowDemoModal} />
    </>
  )
}
